import React from 'react';
import { Card, Col, Descriptions, Row, Space, Statistic, Steps as AntSteps, Tag, Typography, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { CheckedBy, ProxyDiagResponse, ProxyStepResult, checkedFromLabel } from './model';

const { Text } = Typography;

const statLabel = (s: string) => <Text type="secondary" style={{ fontSize: 12 }}>{s}</Text>;
const STAT_VALUE: React.CSSProperties = { fontSize: 18, lineHeight: 1.2 };

const stateColor = (state: string) => {
  switch (state) {
    case 'HEALTHY': return 'green';
    case 'DEGRADED': return 'orange';
    case 'DOWN': return 'red';
    case 'ERROR': return 'red';
    default: return 'default';
  }
};

const stepStatus = (step: ProxyStepResult): 'finish' | 'error' | 'wait' => {
  if (step.success) return 'finish';
  if (step.error?.includes('skipped')) return 'wait';
  return 'error';
};

/** ProxyResultPanel renders the outcome of a proxy connectivity diagnostic:
 *  the vantage point, one stats strip, and a card per proxy with its step
 *  cascade beside the raw facts. */
const ProxyResultPanel: React.FC<{ result: ProxyDiagResponse; checkedBy: CheckedBy | null }> = ({ result, checkedBy }) => {
  const { token } = theme.useToken();
  const vantage = checkedFromLabel(checkedBy);

  return (
    <>
      {/* One line of text does not need an Alert box around it. */}
      {vantage && (
        <Space size={4} style={{ marginBottom: 8 }}>
          <GlobalOutlined style={{ color: token.colorTextSecondary }} />
          <Text type="secondary" style={{ fontSize: 12 }}>Checked from</Text>
          <Text strong style={{ fontSize: 12 }}>{vantage}</Text>
        </Space>
      )}

      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
        <Row gutter={12}>
          <Col span={6}>
            <Statistic title={statLabel('Proxies tested')} value={result.totalProxies} valueStyle={STAT_VALUE} />
          </Col>
          <Col span={6}>
            <Statistic
              title={statLabel('Succeeded')}
              value={result.succeeded}
              valueStyle={{ ...STAT_VALUE, color: token.colorSuccess }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={statLabel('Failed')}
              value={result.failed}
              valueStyle={{ ...STAT_VALUE, color: result.failed > 0 ? token.colorError : token.colorSuccess }}
              prefix={result.failed > 0 ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic title={statLabel('Total duration')} value={result.totalDurationMs} suffix="ms" valueStyle={STAT_VALUE} />
          </Col>
        </Row>
      </Card>

      {result.results.map((r, idx) => (
        <Card
          key={idx}
          title={(
            <Space size={6}>
              <Tag color={stateColor(r.state)} style={{ margin: 0, fontSize: 12 }}>{r.state}</Tag>
              <Text code style={{ fontSize: 14 }}>{r.protocol}://{r.proxy}</Text>
              {r.success
                ? <Tag icon={<CheckCircleOutlined />} color="success" style={{ margin: 0, fontSize: 12 }}>{r.latencyMs.toFixed(0)} ms</Tag>
                : <Tag icon={<CloseCircleOutlined />} color="error" style={{ margin: 0, fontSize: 12 }}>Failed</Tag>}
            </Space>
          )}
          size="small"
          style={{ marginBottom: 10 }}
          styles={{ body: { padding: 12 } }}
        >
          <Row gutter={16}>
            <Col span={14}>
              <AntSteps
                direction="vertical"
                size="small"
                current={-1}
                items={r.steps.map((step) => {
                  const tagColor = !step.success
                    ? 'red'
                    : step.severity === 'warn' ? 'orange'
                    : step.severity === 'info' ? 'blue'
                    : 'green';
                  const tagLabel = !step.success
                    ? 'Failed'
                    : `${step.latency.toFixed(0)} ms${step.severity === 'warn' ? ' · warn' : step.severity === 'info' ? ' · info' : ''}`;
                  const noteColor = step.severity === 'warn'
                    ? token.colorWarning
                    : step.severity === 'info' ? token.colorInfo : token.colorTextSecondary;
                  return {
                    title: (
                      <Space size={6}>
                        <Text strong style={{ fontSize: 13 }}>{step.name}</Text>
                        <Tag color={tagColor} style={{ margin: 0, fontSize: 12 }}>{tagLabel}</Tag>
                      </Space>
                    ),
                    description: (
                      <div>
                        {step.error
                          ? <Text type="danger" style={{ fontSize: 12 }}>{step.error}</Text>
                          : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {step.name === 'proxy_connect' && 'TCP connection to proxy server'}
                              {step.name === 'proxy_request' && `HTTP GET through proxy → ${result.targetURL}`}
                              {step.name === 'proxy_latency' && `Tunnel latency (max ${result.maxLatencyMs} ms)`}
                            </Text>
                          )}
                        {Array.isArray(step.notes) && step.notes.length > 0 && (
                          <div style={{ marginTop: 2 }}>
                            {step.notes.map((n, i) => (
                              <Text key={i} style={{ display: 'block', fontSize: 12, color: noteColor }}>{n}</Text>
                            ))}
                          </div>
                        )}
                      </div>
                    ),
                    status: stepStatus(step),
                  };
                })}
              />
            </Col>
            <Col span={10}>
              <Descriptions
                column={1}
                size="small"
                bordered
                styles={{ label: { fontSize: 12, padding: '4px 8px' }, content: { fontSize: 12, padding: '4px 8px' } }}
              >
                <Descriptions.Item label="Protocol">{r.protocol.toUpperCase()}</Descriptions.Item>
                <Descriptions.Item label="Address">{r.proxy}</Descriptions.Item>
                <Descriptions.Item label="State"><Tag color={stateColor(r.state)} style={{ margin: 0, fontSize: 12 }}>{r.state}</Tag></Descriptions.Item>
                <Descriptions.Item label="Latency">{r.latencyMs > 0 ? `${r.latencyMs.toFixed(1)} ms` : '—'}</Descriptions.Item>
                <Descriptions.Item label="Total duration">{r.durationMs} ms</Descriptions.Item>
                {r.error && <Descriptions.Item label="Error"><Text type="danger" style={{ fontSize: 12 }}>{r.error}</Text></Descriptions.Item>}
              </Descriptions>
            </Col>
          </Row>
        </Card>
      ))}
    </>
  );
};

export default ProxyResultPanel;
